import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import Header from '../../components/ui/Header';
import { decodeProtectedData, PROTECTED_DATA } from '../../utils/obfuscation';
import ProfileSection from './components/ProfileSection';
import HeroContent from './components/HeroContent';
import CTAButtons from './components/CTAButtons';
import SocialLinks from './components/SocialLinks';
import StatsSection from './components/StatsSection';
import ScrollIndicator from './components/ScrollIndicator';
import { HeroData } from './types';

const HeroSection = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const heroData: HeroData = {
    name: t("hero.name"),
    title: t("hero.title"),
    tagline: t("hero.tagline"),
    description: t("hero.description"),
    profileImage: "/profile.png",
    profileImageAlt: t("hero.profileImageAlt"),
    ctaButtons: [
    {
      id: "cta-1",
      label: t("hero.ctaViewWork"),
      variant: "default",
      iconName: "Briefcase",
      action: () => navigate('/portfolio-showcase')
    },
    {
      id: "cta-2",
      label: t("hero.ctaGetInTouch"),
      variant: "outline",
      iconName: "Mail",
      action: () => navigate('/contact-section')
    }],

    socialLinks: [
    {
      id: "social-1",
      platform: "GitHub",
      url: "https://github.com/BMTmohammedtaha",
      iconName: "Github",
      ariaLabel: "Visit GitHub profile"
    },
    {
      id: "social-2",
      platform: "LinkedIn",
      url: "https://linkedin.com/in/bmt-mohammedtaha/",
      iconName: "Linkedin",
      ariaLabel: "Visit LinkedIn profile"
    },
    {
      id: "social-4",
      platform: "Email",
      url: decodeProtectedData(PROTECTED_DATA.emailHref),
      iconName: "Mail",
      ariaLabel: "Send email"
    }],

    stats: [
    {
      id: "stat-1",
      value: "50+",
      label: t("hero.stats.projects"),
      iconName: "CheckCircle"
    },
    {
      id: "stat-2",
      value: "7+",
      label: t("hero.stats.experience"),
      iconName: "Award"
    },
    {
      id: "stat-3",
      value: "100%",
      label: t("hero.stats.satisfaction"),
      iconName: "Star"
    }]

  };

  return (
    <>
      <Helmet>
        <title>Mohammed Taha [BMT] - Full-Stack Developer & System Architect | Mohammed Taha [BMT]</title>
        <meta
          name="description"
          content="Professional full-stack developer specializing in Laravel, React, and comprehensive system architecture. Transforming business challenges into scalable digital solutions." />

        <meta
          name="keywords"
          content="full-stack developer, Laravel developer, React developer, system architect, web development, software engineer" />

        <meta property="og:title" content="Mohammed Taha [BMT] - Full-Stack Developer & System Architect" />
        <meta
          property="og:description"
          content="Crafting digital empires from concept to completion. Expert in Laravel, React, and comprehensive system architecture." />

        <meta property="og:type" content="website" />
      </Helmet>

      <div className="min-h-screen gradient-background">
        <Header />

        <main className="relative pt-24 pb-20 px-4 md:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <section className="flex flex-col items-center justify-center min-h-[calc(100vh-6rem)]">
              <ProfileSection
                profileImage={heroData.profileImage}
                profileImageAlt={heroData.profileImageAlt}
                name={heroData.name} />


              <HeroContent
                name={heroData.name}
                title={heroData.title}
                tagline={heroData.tagline}
                description={heroData.description} />


              <CTAButtons buttons={heroData.ctaButtons} />

              <SocialLinks links={heroData.socialLinks} />

              <StatsSection stats={heroData.stats} />

              {/* <ScrollIndicator /> */}
            </section>
          </div>
        </main>
      </div>
    </>);

};

export default HeroSection;